import { toCrawlableInternalPath } from "./canonicalPath";

export type BreadcrumbStoreItem = {
  title: string;
  to: string;
};

export type BreadcrumbNavigationItem = {
  name: string;
  path: string;
};

const TECHNICAL_SEGMENTS = new Set(["store", "category", "product"]);

export const ROUTE_BREADCRUMB_LABELS: Readonly<Record<string, string>> = {
  about_us: "About Us",
  work_with_us: "Work With Us",
  customer_care: "Customer Care",
  delivery: "Shipping Info",
  refund_policy: "Refund Policy",
  privacy_policy: "Privacy Policy",
  cookies_policy: "Cookies Policy",
  payment_methods: "Payment Methods",
  size_guide: "Size Guide",
  terms: "Terms & Conditions",
  contact: "Contact Us",
  brands: "Brands",
  "glasses-c90": "Glasses",
  "final-sale-eyewear": "Final Sale",
};

type ProductBreadcrumbSource = {
  name?: unknown;
  url?: unknown;
  meta?: {
    title?: unknown;
  } | null;
} | null;

type CategoryBreadcrumbSource = {
  id?: unknown;
  name?: unknown;
  h1?: unknown;
  url?: unknown;
  description?: unknown;
  breadcrumbs?: unknown;
  meta?: {
    title?: unknown;
  } | null;
} | null;

const cleanText = (value: unknown): string => String(value ?? "").trim();

export function normalizeBreadcrumbPath(value: unknown): string {
  const path = String(value || "")
    .split(/[?#]/)[0]
    .replace(/\/+$/, "");
  return path || "/";
}

export function readableBreadcrumbSegment(segment: unknown): string {
  const raw = String(segment || "");
  const decoded = (() => {
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  })();
  const known = ROUTE_BREADCRUMB_LABELS[decoded.toLowerCase()];
  if (known) return known;

  return decoded
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * Deterministic route fallback shared by visible navigation and JSON-LD.
 * Entity pages replace this with their backend-provided canonical hierarchy.
 */
export function buildFallbackRouteBreadcrumbs(
  routePath: unknown
): BreadcrumbNavigationItem[] {
  const segments = normalizeBreadcrumbPath(routePath)
    .split("/")
    .filter(Boolean);
  const trail = segments
    .map((segment, index) => ({
      name: readableBreadcrumbSegment(segment),
      path: `/${segments.slice(0, index + 1).join("/")}`,
      segment: segment.toLowerCase(),
    }))
    .filter((item) => !TECHNICAL_SEGMENTS.has(item.segment))
    .map(({ name, path }) => ({ name, path }));

  return [{ name: "Home", path: "/" }, ...trail];
}

/**
 * Never render a category left in Vuex by the previous SPA route. While the
 * new category request is in flight, derive a small display shell from the
 * already-resolved URL so the breadcrumb and heading are correct immediately.
 */
export function categoryForActiveRoute(
  category: CategoryBreadcrumbSource,
  categoryId: unknown,
  routePath: unknown
): Exclude<CategoryBreadcrumbSource, null> {
  const expectedId = cleanText(categoryId);
  const normalizedPath = normalizeBreadcrumbPath(routePath);
  const categoryPath = normalizeBreadcrumbPath(
    toCrawlableInternalPath(category?.url)
  );
  const matchesId = expectedId && cleanText(category?.id) === expectedId;
  const matchesPath =
    !expectedId && categoryPath !== "/" && categoryPath === normalizedPath;

  if (category && (matchesId || matchesPath)) {
    return category;
  }

  const fallbackTrail = buildFallbackRouteBreadcrumbs(normalizedPath);
  const fallback = fallbackTrail[fallbackTrail.length - 1];
  const name = fallback?.path === "/" ? "" : cleanText(fallback?.name);

  return {
    id: expectedId,
    name,
    h1: name,
    url: normalizedPath,
    description: "",
    breadcrumbs: [],
    meta: null,
  };
}

/**
 * Product breadcrumbs have one canonical display label: product.name.
 * meta.title is only a defensive fallback because it is normally a longer,
 * marketing-oriented page title and is not suitable for visible navigation.
 */
export function buildProductBreadcrumbTrail(
  product: ProductBreadcrumbSource,
  currentPath: string,
  ancestors: BreadcrumbStoreItem[] = []
): BreadcrumbStoreItem[] {
  const normalizedAncestors = ancestors
    .map((item) => ({
      title: cleanText(item?.title),
      to: toCrawlableInternalPath(item?.to),
    }))
    .filter((item) => item.title && item.to);

  const productTitle =
    cleanText(product?.name) || cleanText(product?.meta?.title);
  const productPath =
    toCrawlableInternalPath(product?.url) ||
    toCrawlableInternalPath(currentPath);

  if (!productTitle || !productPath) return normalizedAncestors;

  return [
    ...normalizedAncestors,
    {
      title: productTitle,
      to: productPath,
    },
  ];
}

/** Build one canonical category trail for both visible navigation and JSON-LD. */
export function buildCategoryBreadcrumbTrail(
  category: CategoryBreadcrumbSource,
  currentPath: string,
  ancestors: BreadcrumbStoreItem[] = []
): BreadcrumbStoreItem[] {
  const normalizedAncestors = ancestors
    .map((item) => ({
      title: cleanText(item?.title),
      to: toCrawlableInternalPath(item?.to),
    }))
    .filter((item) => item.title && item.to);
  const categoryTitle =
    cleanText(category?.h1) ||
    cleanText(category?.name) ||
    cleanText(category?.meta?.title);
  const categoryPath =
    toCrawlableInternalPath(category?.url) ||
    toCrawlableInternalPath(currentPath);

  if (!categoryTitle || !categoryPath) return normalizedAncestors;
  return [...normalizedAncestors, { title: categoryTitle, to: categoryPath }];
}

/**
 * Convert the Vuex breadcrumb contract into the format rendered by the
 * navigation component. Keeping this conversion shared ensures the visible
 * trail consumes the same items as BreadcrumbList JSON-LD.
 */
export function toNavigationBreadcrumbs(
  items: BreadcrumbStoreItem[],
  homeName = "Home"
): BreadcrumbNavigationItem[] {
  const trail = (items || [])
    .map((item) => ({
      name: cleanText(item?.title),
      path: toCrawlableInternalPath(item?.to),
    }))
    .filter((item) => item.name && item.path);

  return [{ name: homeName, path: "/" }, ...trail];
}
